import { Link } from 'react-router-dom';
import type { Notification, NotificationType } from '@/types/domain';
import { formatRelativeTime } from '@/lib/format';
import { haptic } from '@/lib/telegram';
import { useMarkRead } from '@/hooks/useNotifications';
import clsx from 'clsx';
import styles from './NotificationItem.module.css';

interface Props {
  notification: Notification;
}

export const NotificationItem = ({ notification }: Props) => {
  const markRead = useMarkRead();
  const isUnread = notification.read_at === null;
  const auctionId = notification.payload?.auction_id as string | undefined;

  const handleClick = () => {
    haptic.light();
    if (isUnread) {
      markRead.mutate(notification.id);
    }
  };

  const content = (
    <>
      <div className={clsx(styles.iconWrap, styles[`icon-${variantForType(notification.type)}`])}>
        <Icon type={notification.type} />
      </div>

      <div className={styles.body}>
        <div className={styles.titleRow}>
          <p className={styles.title}>{notification.title}</p>
          {isUnread && <span className={styles.unreadDot} aria-hidden />}
        </div>
        <p className={styles.text}>{notification.body}</p>
        {notification.payload?.auction_title ? (
          <p className={styles.refLine}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 6l6 6" />
              <path d="M8 12l6-6" />
              <path d="M3 21l8-8" />
              <path d="M16 18h6" />
            </svg>
            {notification.payload.auction_title as string}
          </p>
        ) : null}
        <p className={styles.time}>{formatRelativeTime(notification.created_at)}</p>
      </div>
    </>
  );

  // Если есть auction_id — оборачиваем в Link, иначе просто div
  if (auctionId) {
    return (
      <Link
        to={`/auction/${auctionId}`}
        className={clsx(styles.row, isUnread && styles.rowUnread)}
        onClick={handleClick}
      >
        {content}
      </Link>
    );
  }

  return (
    <div
      className={clsx(styles.row, isUnread && styles.rowUnread)}
      onClick={handleClick}
    >
      {content}
    </div>
  );
};

// ── Маппинг типа на цвет иконки ────────────────────────
const variantForType = (type: NotificationType): string => {
  switch (type) {
    case 'auction_won':
    case 'moderation_approved':
      return 'success';
    case 'auction_lost':
    case 'moderation_rejected':
    case 'seller_refused':
      return 'danger';
    case 'bid_outbid':
    case 'auction_ending_soon':
    case 'deal_confirmation_required':
    case 'deal_deadline_warning':
      return 'warning';
    case 'new_auction_from_subscription':
    case 'auction_published':
      return 'accent';
    default:
      return 'info';
  }
};

// ── Иконки по типам ─────────────────────────────────────
const Icon = ({ type }: { type: NotificationType }) => {
  switch (type) {
    case 'auction_won':
    case 'moderation_approved':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="m5 12 5 5L20 7" />
        </svg>
      );
    case 'auction_lost':
    case 'moderation_rejected':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      );
    case 'bid_outbid':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 17 9 11l4 4 8-8" />
          <path d="M14 7h7v7" />
        </svg>
      );
    case 'auction_ending_soon':
    case 'deal_deadline_warning':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 3" />
        </svg>
      );
    case 'deal_confirmation_required':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
      );
    case 'new_auction_from_subscription':
    case 'auction_published':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
      );
  }
};